import { AnnouncementPreviewItemModel } from "client/Api/Hub/Announcements/announcementViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { ReactElement, useMemo } from "react";
import { CardProps, HubItemStatistic } from "../common";
import { CardBase } from "./CardBase";
import { NumberFormatter } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { isNotNil } from "client/Misc/utils";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";

export type AnnouncementCardProps = CardProps<AnnouncementPreviewItemModel>;

export const AnnouncementCard = (props: AnnouncementCardProps): ReactElement => {
	const {
		item,
		size,
		onOpen,
		hasTopBarBackdrop,
		shouldHideSecondaryInfo = false,
		timezone
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(item.reactionCounters);

	const statistics = useMemo(
		(): readonly HubItemStatistic[] => {
			const result: HubItemStatistic[] = [];

			result.push({
				icon: reactionStatistic.icon,
				text: statisticsFormatter(reactionStatistic.total),
				trackingId: "reaction-count"
			});

			if (item.areCommentsEnabled) {
				result.push({
					icon: "comment",
					text: statisticsFormatter(item.commentsCount),
					trackingId: "comment-count"
				});
			}

			return result;
		},
		[
			item.areCommentsEnabled,
			item.commentsCount,
			reactionStatistic.icon,
			reactionStatistic.total,
			statisticsFormatter
		]
	);

	const categoryNames = useMemo(
		(): readonly string[] => {
			return item.categories
				.map(x => x.name);
		},
		[item.categories]
	);

	return (
		<CardBase
			itemKind={HubItemKind.Announcement}
			id={item.id}
			title={item.title}
			media={item.media}
			startDate={item.publicationStart}
			statistics={statistics}
			categoryNames={categoryNames}
			size={size}
			onOpen={onOpen}
			hasTopBarBackdrop={hasTopBarBackdrop}
			isPinned={isNotNil(item.pinDate)}
			timezone={timezone}
			shouldHideSecondaryInfo={shouldHideSecondaryInfo}
			isNew={item.isNew}
		/>
	);
};
AnnouncementCard.displayName = "AnnouncementCard";
